import * as React from 'react';
import { styled } from 'stitches.config';
import { useIntl, FormattedMessage } from 'react-intl';
import Typography from '@/components/sushi/Typography';
import { useWealthSimulatorCalculation } from '@/hooks/wealthSimulator/useWealthSimulatorCalculation';
import { FormValues } from './types';
import { Graph } from './Graph';
import { SimulatorShareResult } from '../simulators/SimulatorShareResult';
import { getFormattedValue } from '@/utils/simulators';

interface ResultsProps {
  values: FormValues;
}



export const Results = ({ values }: ResultsProps) => {
  const { formatMessage } = useIntl();
  const { futurCapital, addedValue, netValue, monthlyRevenue, graphData, isInvalid } =
    useWealthSimulatorCalculation(values);

  return (
    <GraphInfos>
      <GraphTitle variant="headingXXS" color="secondaryAlt">
        <FormattedMessage
          id="wealth_simulator.calculated_infos.title"
          values={{
            // @ts-ignore
            years: (chunks) => (
              <span>
                {values?.nbYears} {chunks}
              </span>
            ),
            // @ts-ignore
            netValue: () => (
              <span className="text-white">{getFormattedValue(netValue, isInvalid)}</span>
            ),
            // @ts-ignore
            monthlyRevenue: (chunks) => (
              <span className="text-white">{`${getFormattedValue(
                monthlyRevenue,
                isInvalid
              )}/${chunks}`}</span>
            ),
          }}
        />
      </GraphTitle>

      <Graph displayPlaceholder={isInvalid} graphData={graphData} nbYears={values.nbYears} />

      <GraphSubtitle variant="subHeadingM" color="secondaryAlt">
        <FormattedMessage
          id="wealth_simulator.calculated_infos.subtitle"
          values={{
            // @ts-ignore
            totalInvestment: () => (
              <span className="text-white">
                {getFormattedValue(values.annualInvestment * values.nbYears, isInvalid)}
              </span>
            ),
            // @ts-ignore
            interests: () => (
              <span className="text-white">
                {getFormattedValue(
                  netValue - values.annualInvestment * values.nbYears - values.patrimony,
                  isInvalid
                )}
              </span>
            ),
            // @ts-ignore
            monthlyRevenue: () => (
              <span className="text-white">{getFormattedValue(monthlyRevenue, isInvalid)}</span>
            ),
            // @ts-ignore
            years: () => <span>{values?.nbYears}</span>,
          }}
        />
      </GraphSubtitle>
      <InfoGrid>
        <div>
          <Typography variant="bodyS" color="secondaryAlt">
            {formatMessage({ id: 'wealth_simulator.calculated_infos.infoLabel1' })}
          </Typography>
          <Typography variant="bodyM">{getFormattedValue(futurCapital, isInvalid)}</Typography>
        </div>
        <div>
          <Typography variant="bodyS" color="secondaryAlt">
            {formatMessage({ id: 'wealth_simulator.calculated_infos.infoLabel2' })}
          </Typography>
          <Typography variant="bodyM">{getFormattedValue(addedValue, isInvalid)}</Typography>
        </div>
        <div>
          <Typography variant="bodyS" color="secondaryAlt">
            {formatMessage({ id: 'wealth_simulator.calculated_infos.infoLabel3' })}
          </Typography>
          <Typography variant="bodyM">{getFormattedValue(netValue, isInvalid)}</Typography>
        </div>
        <div>
          <Typography variant="bodyS" color="secondaryAlt">
            {formatMessage({ id: 'wealth_simulator.calculated_infos.infoLabel4' })}
          </Typography>
          <Typography variant="bodyM">{getFormattedValue(monthlyRevenue, isInvalid)}</Typography>
        </div>
      </InfoGrid>
      <SimulatorShareResult />
    </GraphInfos>
  );
};

const GraphInfos = styled('div', {
  padding: '48px 0',
  borderBottom: '1px solid #343434',
  '> *:first-child': {
    marginBottom: '32px',
  },
  '@sm': {
    padding: '40px 0',
    borderBottom: 'none',
  },
});

const GraphTitle = styled(Typography, {
  fontSize: '22px !important',
  padding: '0 48px',
  '@sm': {
    padding: '0 32px',
  },
});

const GraphSubtitle = styled(Typography, {
  letterSpacing: '0px !important',
  padding: '0 48px',
  '@sm': {
    padding: '0 32px',
  },
});

const InfoGrid = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '22px',
  padding: '0 48px',
  marginTop: '$8',
  marginBottom: '$8',
  '*': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  '@sm': {
    padding: '0 $8',
  },
});


